import React, { useState, useContext, useMemo, useEffect } from "react";
import { formatPropAddr } from "../../utils/propertyAddr";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, btnP, btnSec, bdg, inpS, sp, rad } from "../../constants/styles";
import { gradCardStyle, gradLift, glassPill, isLightTone, SLATE_INK } from "../../constants/gradCard";
import Fld from "../common/Fld";
import Ic from "../common/Ic";
import SignaturePad from "./SignaturePad";
import PropertyCondition from "../common/PropertyCondition";
import { defaultTerms, generateAgreementHTML, buildAgreementDataFromLease, buildSigHTML, formatDateDMY, AVAILABLE_LANGUAGES } from "../../utils/agreement";
import { generateSettlementHTML } from "../../utils/settlementPdf";
import { getLeaseStatus, getDaysRemaining, isExpiringSoon, leaseStatusColor, leaseStatusLabel } from "../../utils/leaseHelpers";
import { IS_DESKTOP } from "../../constants/platform";

// Snap-first tenant resolver. The lease may have terms.tenant_snap
// (frozen at sign/activate/settle by server/utils/leaseSnapshot) —
// when present, it's the legal record and we MUST prefer it over the
// live `tens.find()` result, which can go blank after the tenant is
// detached / deleted / has their profile edited. Falls back to the
// live row for old leases without a snap.
function resolveTenantIdentity(lease, liveTen) {
  if (!lease) return null;
  let terms = {};
  try {
    terms = typeof lease.terms === 'string' ? (lease.terms ? JSON.parse(lease.terms) : {}) : (lease.terms || {});
  } catch (_) { terms = {}; }
  const snap = terms.tenant_snap || null;
  return {
    name:        snap?.name        || liveTen?.name        || '',
    ph:          snap?.phone       || liveTen?.ph          || '',
    phone:       snap?.phone       || liveTen?.ph          || '',
    parent_name: snap?.parent_name || liveTen?.parent_name || '',
    relation:    snap?.relation    || liveTen?.relation    || 'son of',
    id_number:   snap?.id_number   || liveTen?.id_number   || '',
    aad:         snap?.id_number   || liveTen?.aad         || '',
    address:     snap?.address     || liveTen?.address     || '',
    join_date:   snap?.join_date   || liveTen?.join_date   || '',
    esign:       snap?.esign       || liveTen?.esign       || '',
    // Marker: tells the UI whether this row is reading from a frozen
    // snap (safe forever) or only from the live tenants row (volatile).
    _fromSnap:   !!snap,
  };
}

// Wrapper around Fld for clause fields: always shows an English input,
// and (behind a toggle) a per-language translation input. Value is stored
// as either a plain string (legacy / no translation) or an object
// { en: "...", [langCode]: "..." } when the landlord provides a
// translation. resolveClause() in clauseLibrary.js reads both shapes.
function ClauseFld({ h, l, v, set }) {
  const valObj = (typeof v === 'object' && v !== null) ? v : null;
  const en = valObj ? (valObj.en || '') : (v || '');
  const existingLang = valObj ? Object.keys(valObj).find(k => k !== 'en') : null;
  const existingTrans = existingLang ? (valObj[existingLang] || '') : '';

  const [showTrans, setShowTrans] = useState(!!existingLang);
  const [selLang, setSelLang] = useState(existingLang || 'hi');

  const writeEn = (newEn) => {
    // If a translation slot is open with content, keep as object.
    if (showTrans && existingTrans) {
      set({ en: newEn, [selLang]: existingTrans });
    } else {
      set(newEn || '');
    }
  };

  const writeTrans = (newTrans) => {
    if (newTrans) {
      set({ en, [selLang]: newTrans });
    } else if (en) {
      // Translation cleared — collapse back to plain string.
      set(en);
    } else {
      set('');
    }
  };

  const changeLang = (newLang) => {
    // Move the existing translation text under the new language key.
    if (valObj && existingLang && existingTrans) {
      const obj = { en, [newLang]: existingTrans };
      set(obj);
    }
    setSelLang(newLang);
  };

  const removeTrans = () => {
    setShowTrans(false);
    set(en || '');
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <Fld h={h} l={l + " (English)"} v={en} set={writeEn} multiline />
      {showTrans ? (
        <div style={{
          marginTop: -4, marginBottom: 8, marginLeft: 12,
          paddingLeft: 12, paddingTop: 8,
          borderLeft: `2px solid ${h.ac}40`,
        }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: h.sc, fontWeight: 600 }}>Translate to:</span>
            <select
              value={selLang}
              onChange={(e) => changeLang(e.target.value)}
              style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx }}
            >
              {AVAILABLE_LANGUAGES.filter(o => o.code !== 'en').map(o => (
                <option key={o.code} value={o.code}>{o.label} / {o.nativeLabel}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={removeTrans}
              style={{ background: "none", border: "none", color: h.rd, fontSize: 11, cursor: "pointer", padding: 0 }}
            >
              × Remove translation
            </button>
          </div>
          <Fld h={h} l={null} v={existingTrans} set={writeTrans} multiline ph="Paste or type your translation here…" />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowTrans(true)}
          style={{ background: "none", border: "none", color: h.ac, fontSize: 11, fontWeight: 600, cursor: "pointer", padding: "0 0 8px 0" }}
        >
          + Add translation
        </button>
      )}
    </div>
  );
}

/* ─── iOS-style pencil (SVG, 2px stroke — not an emoji) ──────────────── */
const PencilIcon = ({ color = "currentColor", size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z" />
  </svg>
);

/* ─── Collapsible section ─────────────────────────────────────────────
   iOS information architecture: one hero, everything else disclosed on
   demand. The Edit affordance deliberately lives INSIDE the expanded
   body — a row of pencils on collapsed headers is visual noise, and you
   can't meaningfully edit what you can't see. */
function Section({ h, icon, title, summary, onEdit, editLabel = "Edit", children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: h.cd, borderRadius: 18, boxShadow: h.sh, marginBottom: 10, overflow: "hidden" }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
          display: "flex", alignItems: "center", gap: 11, padding: "13px 15px", textAlign: "left",
          WebkitTapHighlightColor: "transparent",
        }}>
        {icon && <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", color: h.tx, fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.1px" }}>{title}</span>
          {summary && !open && (
            <span style={{ display: "block", color: h.ts, fontSize: 11, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</span>
          )}
        </span>
        <span style={{ color: h.tt || h.ts, fontSize: 12, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .22s cubic-bezier(.32,.72,.22,1)" }}>{"▾"}</span>
      </button>
      {open && (
        <div style={{ padding: "0 15px 14px" }}>
          {onEdit && (
            <button onClick={onEdit} style={{
              display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 10,
              background: `${h.ac}0f`, border: "none", borderRadius: 999, padding: "6px 12px",
              color: h.a2, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            }}>
              <PencilIcon color={h.a2} /> {editLabel}
            </button>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

/* ─── Agreement readiness ─────────────────────────────────────────────
   One line, dot-and-track, directly under the hero. Tells the landlord
   how complete this agreement is and — more usefully — exactly what is
   still missing, without opening a single section. */
function Readiness({ h, steps, onEdit }) {
  const done = steps.filter(s => s.done).length;
  const pct = steps.length ? Math.round((done / steps.length) * 100) : 0;
  const dim = h.dk ? "rgba(255,255,255,0.16)" : "rgba(17,24,39,0.12)";
  const tone = pct === 100 ? h.gn : h.a2;
  // The whole strip is the control: tap it to go straight to editing.
  // Each dot still names its checkpoint on hover / long-press.
  return (
    <button onClick={onEdit} aria-label={`Agreement ${pct}% complete — edit`} style={{
      width: "100%", textAlign: "left", fontFamily: "inherit", cursor: "pointer", border: "none",
      background: h.cd, borderRadius: 18, boxShadow: h.sh, padding: "13px 15px", marginBottom: 10,
      WebkitTapHighlightColor: "transparent",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ color: h.tx, fontSize: 12.5, fontWeight: 600, letterSpacing: "-0.1px" }}>Agreement readiness</span>
        <span style={{ color: tone, fontSize: 13, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
      </div>
      {/* Dot per checkpoint with its own short label underneath, so a grey
          dot is self-explaining — no separate "still needed" sentence. */}
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        {steps.map((s, i) => (
          <div key={i} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
              <span style={{ flex: 1, height: 2, background: i === 0 ? "transparent" : (steps[i - 1].done ? h.gn : dim), transition: "background .25s" }} />
              <span title={`${s.label}${s.done ? " — done" : " — still needed"}`} style={{
                width: 9, height: 9, borderRadius: "50%", flexShrink: 0,
                background: s.done ? h.gn : dim, transition: "background .25s",
              }} />
              <span style={{ flex: 1, height: 2, background: i === steps.length - 1 ? "transparent" : (s.done ? h.gn : dim), transition: "background .25s" }} />
            </div>
            <span style={{
              marginTop: 6, fontSize: 8.5, fontWeight: s.done ? 500 : 600,
              color: s.done ? (h.tt || h.ts) : h.a2,
              maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{s.short}</span>
          </div>
        ))}
      </div>
    </button>
  );
}

/* ─── Witnesses ───────────────────────────────────────────────────────
   Witnesses are OPTIONAL. Rendering four empty slots implied four were
   required and made the page feel like a form to complete. Now: already-
   filled witnesses always show, and "+ Add witness" reveals the next slot
   one at a time (max 4 — 2 landlord-side, 2 tenant-side). */
function Witnesses({ h, sel, terms, handleWitName, handleWitSign, showWitSigPad, setShowWitSigPad, SigPad }) {
  const defs = [
    { label: "Witness 1 (Landlord)", name: terms.witness1_name, nameKey: "witness1_name", addr: terms.witness1_addr, sigKey: "witness1_sig", sig: terms.witness1_sig, padKey: "w1" },
    { label: "Witness 2 (Landlord)", name: terms.witness2_name, nameKey: "witness2_name", addr: terms.witness2_addr, sigKey: "witness2_sig", sig: terms.witness2_sig, padKey: "w2" },
    { label: "Witness 1 (Tenant)", name: terms.ten_witness1_name, nameKey: "ten_witness1_name", addr: terms.ten_witness1_addr, sigKey: "ten_witness1_sig", sig: terms.ten_witness1_sig, padKey: "tw1" },
    { label: "Witness 2 (Tenant)", name: terms.ten_witness2_name, nameKey: "ten_witness2_name", addr: terms.ten_witness2_addr, sigKey: "ten_witness2_sig", sig: terms.ten_witness2_sig, padKey: "tw2" },
  ];
  // Never hide a witness that already has data.
  const lastFilled = defs.reduce((acc, w, i) => (w.name || w.sig) ? i : acc, -1);
  const [revealed, setRevealed] = useState(0);
  const shown = Math.max(lastFilled + 1, revealed);

  return (
    <Section h={h} icon="🖊️" title="Witnesses"
      summary={lastFilled >= 0 ? `${defs.filter(w => w.name || w.sig).length} added` : "Optional"}>
      {shown === 0 && (
        <div style={{ color: h.ts, fontSize: 11.5, lineHeight: 1.5, marginBottom: 10 }}>
          Witnesses are optional. Add them only if you want their names and signatures on the printed agreement.
        </div>
      )}
      {defs.slice(0, shown).map((w, i) => (
        <div key={i} style={{ marginBottom: 8, padding: 11, borderRadius: 14, background: h.dk ? "rgba(255,255,255,0.04)" : "rgba(17,24,39,0.03)" }}>
          <div style={{ color: h.tx, fontSize: 11.5, fontWeight: 600, marginBottom: 7 }}>{w.label}</div>
          <input
            key={`${sel.id}-${w.nameKey}`}
            defaultValue={w.name || ""}
            placeholder="Witness full name"
            maxLength={60}
            onBlur={(e) => handleWitName(w.nameKey, e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            style={{ width: "100%", padding: "9px 11px", borderRadius: 11, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 12, boxSizing: "border-box", outline: "none" }}
          />
          {w.addr && <div style={{ color: h.ts, fontSize: 10, marginTop: 5 }}>{w.addr}</div>}
          {w.sig && w.sig.startsWith("sig|") ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ background: `${h.gn}10`, padding: 9, borderRadius: 11 }}>
                <div style={{ color: h.gn, fontSize: 10, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{"✓"} Signed</div>
                <img src={w.sig.split("|").slice(2).join("|")} alt="Witness signature" style={{ maxHeight: 40, background: "#fff", padding: 4, borderRadius: 6, display: "block" }} />
                <div style={{ color: h.ts, fontSize: 9, marginTop: 3 }}>Date: {w.sig.split("|")[1]}</div>
              </div>
              <button onClick={() => setShowWitSigPad(w.padKey)} style={{ background: "none", border: "none", color: h.a2, cursor: "pointer", fontSize: 11, fontWeight: 600, padding: "6px 0 0" }}>Re-sign</button>
            </div>
          ) : (
            <button onClick={() => setShowWitSigPad(w.padKey)} style={{
              width: "100%", marginTop: 8, padding: "9px", borderRadius: 11, border: "none",
              background: `${h.ac}0f`, color: h.a2, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            }}>Draw signature</button>
          )}
          {showWitSigPad === w.padKey && <div style={{ marginTop: 8, border: `1px solid ${h.ac}40`, borderRadius: 12, padding: 10 }}>
            <SigPad h={h} onSave={(data) => handleWitSign(w.sigKey, data)} onCancel={() => setShowWitSigPad(null)} label={w.label} />
          </div>}
        </div>
      ))}
      {shown < defs.length && (
        <button onClick={() => setRevealed(shown + 1)} style={{
          width: "100%", padding: "10px", borderRadius: 12, border: `1px dashed ${h.bd}`,
          background: "none", color: h.ts, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
        }}>+ Add witness</button>
      )}
    </Section>
  );
}

function AgreementPage() {
  const { t, h, leases, setLeases, props, tens, setTens, go, flash, showConfirm, settings, setSettings, orgName, selLeaseId, setSelLeaseId } = useContext(Ctx);
  const parseLLInfo = () => { try { return JSON.parse(settings?.landlord_info || '{}'); } catch { return {}; } };
  const savedLLInfo = parseLLInfo();
  const [view, setView] = useState("list"); // list | detail | edit | document
  const [selId, setSelId] = useState(null);
  // Deep-link from PropDetail's "View Lease" — it sets selLeaseId in
  // context before navigating here. Open that lease's detail directly
  // (previously the id was never read and the user landed on the
  // unfiltered list). Consumed once, then cleared.
  useEffect(() => {
    if (selLeaseId && leases.some(l => l.id === selLeaseId)) {
      setSelId(selLeaseId);
      setView("detail");
      if (setSelLeaseId) setSelLeaseId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [docHtml, setDocHtml] = useState("");  // rendered agreement HTML for the in-app viewer
  const [tab, setTab] = useState("all");
  const [showLLInfoEdit, setShowLLInfoEdit] = useState(false);
  const [llInfo, setLLInfo] = useState(savedLLInfo);
  const setLI = (k, v) => setLLInfo(p => ({ ...p, [k]: v }));
  const [showLLSigPad, setShowLLSigPad] = useState(false);
  const [showTenSigPad, setShowTenSigPad] = useState(false);
  const [showWitSigPad, setShowWitSigPad] = useState(null); // null | 'w1' | 'w2' | 'tw1' | 'tw2'
  const [showLangPicker, setShowLangPicker] = useState(false);

  // Language selection for PDF generation. Defaults to English + Tamil
  // to match the behaviour before language selection was added. Persisted
  // to localStorage so a landlord who picks EN+HI once keeps that choice
  // across sessions.
  const [pdfLangs, setPdfLangs] = useState(() => {
    try {
      const raw = localStorage.getItem('pdfLangs');
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch { /* ignore */ }
    return ['en', 'ta'];
  });
  // ── Selected lease + related data ──
  const sel = leases.find(l => l.id === selId);
  // Live tenant row (may go undefined after detach/delete).
  const selTenLive = sel ? tens.find(x => x.id === sel.tenant_id) : null;
  // Snap-first identity. After a lease is signed/activated/settled the
  // server freezes the tenant's identity onto lease.terms.tenant_snap;
  // this view reads from the snap so the agreement stays intact even
  // after termination. For pre-snap leases falls back to live tens.
  const selTen = resolveTenantIdentity(sel, selTenLive);
  const selProp = sel ? props.find(x => x.id === sel.property_id) : null;

  // ── PDF language picker, source-of-truth handling ──
  // Read order: lease.terms.pdf_languages → localStorage → ['en','ta'] default.
  // The lease wins because the tenant's app reads from there too — without
  // server-side persistence the tenant always gets the default en+ta no
  // matter what the landlord picked. We still write localStorage for the
  // "no lease selected yet" case (e.g. opening a new draft) so the picker
  // remembers across sessions, but the lease-stored value supersedes it
  // when one exists.
  const _parseLeaseTerms = (l) => { try { return typeof l?.terms === 'string' ? JSON.parse(l.terms) : (l?.terms || {}); } catch { return {}; } };
  // Re-hydrate the picker when the user opens a different lease.
  useEffect(() => {
    if (!sel) return;
    const t = _parseLeaseTerms(sel);
    if (Array.isArray(t.pdf_languages) && t.pdf_languages.length > 0) {
      setPdfLangs(t.pdf_languages);
    }
    // If the lease has no saved selection yet, leave whatever the picker
    // already shows (from localStorage or default) — the next toggle will
    // commit it onto the lease.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel?.id]);

  const toggleLang = (code) => {
    setPdfLangs(prev => {
      const next = prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code];
      // Never allow zero languages — fall back to English to avoid a
      // silently empty PDF.
      const final = next.length > 0 ? next : ['en'];
      try { localStorage.setItem('pdfLangs', JSON.stringify(final)); } catch { /* ignore */ }
      // Persist onto the currently selected lease so the tenant's PDF
      // mirrors the choice. Optimistic local update + best-effort save;
      // a save failure leaves the local copy ahead of server, which the
      // next sync will reconcile (and the toggle is cheap to re-do).
      if (sel?.id) {
        const newTerms = { ..._parseLeaseTerms(sel), pdf_languages: final };
        const updatedLease = { ...sel, terms: JSON.stringify(newTerms) };
        setLeases(leases.map(l => l.id === sel.id ? updatedLease : l));
        api.saveLease(updatedLease).catch(() => { /* best-effort */ });
      }
      return final;
    });
    // Brief confirmation so the user knows the toggle persisted without
    // having to look for a save button. Stays a single line — "Languages
    // updated ✓" — to read at a glance.
    flash(t.languagesUpdated || "Languages updated ✓");
  };

  // ── Edit state (only when view === 'edit') ──
  const [editLease, setEditLease] = useState({});
  const [editTerms, setEditTerms] = useState({});
  const setEL = (k, v) => setEditLease(p => ({ ...p, [k]: v }));
  const setET = (k, v) => setEditTerms(p => ({ ...p, [k]: v }));

  const parseTerms = (l) => { try { return typeof l?.terms === 'string' ? JSON.parse(l.terms) : (l?.terms || {}); } catch { return {}; } };

  const openDetail = (l) => { setSelId(l.id); setView("detail"); };
  const openEdit = () => {
    if (!sel) return;
    setEditLease({ start_date: sel.start_date || '', end_date: sel.end_date || '', rent: sel.rent || 0, deposit: sel.deposit || 0, notice_period_days: sel.notice_period_days || 30, lock_in_months: sel.lock_in_months || '', escalation_pct: sel.escalation_pct || '', escalation_interval_months: sel.escalation_interval_months || 12, lease_type: sel.lease_type || 'residential', status: sel.status || 'draft' });
    // Auto-populate landlord info from Settings if not already in terms
    const existingTerms = parseTerms(sel);
    const mergedTerms = { ...defaultTerms, ...existingTerms };
    if (savedLLInfo.name && !existingTerms.landlord_name) {
      mergedTerms.landlord_name = savedLLInfo.name;
      mergedTerms.ll_parent = savedLLInfo.parent || '';
      mergedTerms.ll_relation = savedLLInfo.relation || 'son of';
      mergedTerms.ll_aadhaar = savedLLInfo.aadhaar || '';
      mergedTerms.landlord_address = savedLLInfo.address || '';
      mergedTerms.district = savedLLInfo.district || '';
      mergedTerms.taluk = savedLLInfo.taluk || '';
      mergedTerms.state = savedLLInfo.state || 'Tamil Nadu';
    }
    // Auto-populate tenant-side fields from the tenant's own profile
    // (set by the tenant on their Profile page). Only fills slots that
    // are empty in the lease's cached terms — so if the landlord has
    // already typed something manually, we respect that. This matters
    // for leases that were drafted BEFORE the tenant filled their info:
    // the cached terms have empty strings; we refresh them here.
    if (selTen) {
      const landlordHasEditedTenantInfo = !!(existingTerms.ten_parent);
      if (!existingTerms.ten_parent && selTen.parent_name) {
        mergedTerms.ten_parent = selTen.parent_name;
      }
      // Relation: if the lease was auto-created before the tenant set
      // theirs, the cached value is the literal default 'son of'. Treat
      // that as "not landlord-edited" unless ten_parent is also filled.
      if (!landlordHasEditedTenantInfo && selTen.relation) {
        mergedTerms.ten_relation = selTen.relation;
      }
      if (!existingTerms.ten_aadhaar && selTen.id_number) {
        const last4 = String(selTen.id_number).replace(/\D/g, '').slice(-4);
        if (last4) mergedTerms.ten_aadhaar = `XXXX XXXX ${last4}`;
      }
      if (!existingTerms.ten_address && selTen.address) {
        mergedTerms.ten_address = selTen.address;
      }
    }
    setEditTerms(mergedTerms);
    setView("edit");
  };
  const saveEdit = async () => {
    // Always inject latest landlord info from Settings
    const finalTerms = { ...editTerms };
    if (savedLLInfo.name) {
      finalTerms.landlord_name = savedLLInfo.name;
      finalTerms.ll_parent = savedLLInfo.parent || finalTerms.ll_parent;
      finalTerms.ll_relation = savedLLInfo.relation || finalTerms.ll_relation;
      finalTerms.ll_aadhaar = savedLLInfo.aadhaar || finalTerms.ll_aadhaar;
      finalTerms.landlord_address = savedLLInfo.address || finalTerms.landlord_address;
      finalTerms.district = savedLLInfo.district || finalTerms.district;
      finalTerms.taluk = savedLLInfo.taluk || finalTerms.taluk;
      finalTerms.state = savedLLInfo.state || finalTerms.state;
    }
    const updated = { ...sel, ...editLease, terms: JSON.stringify(finalTerms) };
    setLeases(leases.map(l => l.id === sel.id ? updated : l));
    await api.saveLease(updated);

    // Backfill the tenant's profile with what the landlord just typed
    // in "Tenant Agreement Details" \u2014 parent_name, relation, aadhaar,
    // address. Server-side rule is "fill empty only" (never overwrites
    // existing tenant-entered data), so this is safe to fire on every
    // save. The user benefit: future leases / renewals for this tenant
    // auto-populate without retyping, and the tenant's profile becomes
    // a proper canonical record over time. Best-effort \u2014 a failure here
    // doesn't undo the lease save (the lease.terms.ten_* fields are
    // the legal source of truth for THIS agreement either way).
    if (sel.tenant_id) {
      try {
        const r = await api.updateTenantAgreementFields(sel.tenant_id, {
          parent_name: finalTerms.ten_parent || '',
          relation:    finalTerms.ten_relation || '',
          id_number:   finalTerms.ten_aadhaar || '',
          address:     finalTerms.ten_address || '',
        });
        // Optimistically refresh local tens so the readiness checklist
        // + resolver immediately reflect the new values. We only touch
        // the fields the server confirmed it updated; if r.success is
        // false, fall through silently \u2014 the lease save still landed.
        if (r?.success && r.updated > 0) {
          setTens(tens.map(t => t.id === sel.tenant_id ? {
            ...t,
            parent_name: t.parent_name || finalTerms.ten_parent || t.parent_name,
            relation:    t.relation    || finalTerms.ten_relation || t.relation,
            address:     t.address     || finalTerms.ten_address || t.address,
            id_number:   t.id_number   || (
              finalTerms.ten_aadhaar && !/[xX]/.test(finalTerms.ten_aadhaar)
                ? (String(finalTerms.ten_aadhaar).replace(/\D/g, '').slice(-4) || t.id_number)
                : t.id_number
            ),
          } : t));
        }
      } catch (_) { /* best-effort */ }
    }

    flash("Agreement saved \u2705");
    setView("detail");
  };

  // ── Filter leases ──
  const filtered = useMemo(() => {
    let f = leases || [];
    if (tab === "active") f = f.filter(l => l.status === "active");
    else if (tab === "draft") f = f.filter(l => l.status === "draft");
    else if (tab === "expiring") f = f.filter(l => isExpiringSoon(l));
    else if (tab === "expired") f = f.filter(l => l.status === "expired" || l.status === "terminated");
    return f;
  }, [leases, tab]);

  const stats = useMemo(() => ({
    active: (leases || []).filter(l => l.status === "active").length,
    expiring: (leases || []).filter(l => isExpiringSoon(l)).length,
    expired: (leases || []).filter(l => l.status === "expired" || l.status === "terminated").length,
    draft: (leases || []).filter(l => l.status === "draft").length,
  }), [leases]);

  // ── Settlement Receipt fetch + open ──
  // For terminated leases that went through the vacate flow, surface
  // a "View Settlement Receipt" button that opens the immutable
  // deposit_settlements record as a print-ready PDF page. Same Blob-URL
  // pattern as printAgreement below — no server-side PDF library.
  // State is local (per AgreementPage mount) — we re-fetch on each
  // click instead of pre-loading, since most leases aren't terminated
  // and the data isn't needed unless the user asks for it.
  const [openingReceipt, setOpeningReceipt] = useState(false);
  const openSettlementReceipt = async (lease) => {
    if (!lease) return;
    setOpeningReceipt(true);
    try {
      const r = await api.vacateSettlementByLease(lease.id);
      if (!r?.success) {
        flash(r?.message || "No settlement on file for this lease", "error");
        return;
      }
      // Use snap-resolved tenant/property for the header — the
      // settlement row already carries *_snap fields for legal copy,
      // but generateSettlementHTML accepts optional tenant/property
      // for header-only fallback.
      const liveTen = tens.find(x => x.id === lease.tenant_id);
      const tenant = resolveTenantIdentity(lease, liveTen);
      const property = props.find(x => x.id === lease.property_id);
      const html = generateSettlementHTML({
        settlement: r.settlement,
        vacate_request: r.vacate_request,
        deductions: r.deductions || [],
        photos: r.photos || [],
        lease,
        tenant: tenant ? { name: tenant.name, ph: tenant.ph } : null,
        property,
        org: { name: orgName },
      });
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'noopener');
      if (!w && !/safari/i.test(navigator.userAgent)) {
        flash("Allow popups to view receipt", "error");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      flash("Could not open settlement receipt", "error");
    } finally {
      setOpeningReceipt(false);
    }
  };

  // ── Build the agreement HTML for a lease (shared by the in-app
  //    viewer and the PDF/print path). Returns the full HTML string,
  //    or null if required data is missing.
  const buildLeaseHTML = (lease) => {
    const tenLiveB = tens.find(x => x.id === lease.tenant_id);
    const tenB = resolveTenantIdentity(lease, tenLiveB);
    const propB = props.find(x => x.id === lease.property_id);
    let mergedLeaseB = lease;
    if (savedLLInfo && savedLLInfo.name) {
      let baseTermsB = {};
      try {
        baseTermsB = typeof lease.terms === 'string'
          ? (lease.terms ? JSON.parse(lease.terms) : {})
          : (lease.terms || {});
      } catch (_) { baseTermsB = {}; }
      const overlaidB = {
        ...baseTermsB,
        landlord_name:    savedLLInfo.name    || baseTermsB.landlord_name,
        ll_parent:        savedLLInfo.parent  || baseTermsB.ll_parent,
        ll_relation:      savedLLInfo.relation || baseTermsB.ll_relation,
        ll_aadhaar:       savedLLInfo.aadhaar || baseTermsB.ll_aadhaar,
        landlord_address: savedLLInfo.address || baseTermsB.landlord_address,
        district:         savedLLInfo.district || baseTermsB.district,
        taluk:            savedLLInfo.taluk   || baseTermsB.taluk,
        state:            savedLLInfo.state   || baseTermsB.state,
      };
      mergedLeaseB = { ...lease, terms: JSON.stringify(overlaidB) };
    }
    const dataB = buildAgreementDataFromLease(mergedLeaseB, tenB, propB);
    if (!dataB) return null;
    return generateAgreementHTML(dataB, pdfLangs);
  };

  // ── In-app agreement viewer: render the formatted agreement inside
  //    the app (no download) via an iframe. Opens the "document" view.
  const viewAgreementInApp = (lease) => {
    const html = buildLeaseHTML(lease);
    if (!html) { flash("Missing data to view agreement", "error"); return; }
    setDocHtml(html);
    setView("document");
  };

  // ── PDF Print ──
  const printAgreement = (lease) => {
    // Resolve identity snap-first so the PDF stays intact even after
    // termination/detach. buildAgreementDataFromLease itself is also
    // snap-aware, but we pass the resolved view for consistency with
    // older callers.
    const tenLive = tens.find(x => x.id === lease.tenant_id);
    const ten = resolveTenantIdentity(lease, tenLive);
    const prop = props.find(x => x.id === lease.property_id);
    // Always overlay the latest savedLLInfo onto lease.terms before
    // PDF generation. Reason: the Edit-Save path already copies
    // savedLLInfo into terms, but if the landlord configured their
    // info AFTER drafting the lease (or never opened Edit), terms
    // still has empty landlord_name and the PDF falls back to the
    // dev default ("Rajieev"). This overlay keeps the PDF in sync
    // with current Settings without forcing a redundant Edit-Save.
    let mergedLease = lease;
    if (savedLLInfo && savedLLInfo.name) {
      let baseTerms = {};
      try {
        baseTerms = typeof lease.terms === 'string'
          ? (lease.terms ? JSON.parse(lease.terms) : {})
          : (lease.terms || {});
      } catch (_) { baseTerms = {}; }
      // Settings values WIN over older lease.terms values — the
      // landlord's current saved info is the source of truth for
      // landlord identity. Other fields (tenant_*, clauses, sigs)
      // stay exactly as the lease has them.
      const overlaid = {
        ...baseTerms,
        landlord_name:    savedLLInfo.name    || baseTerms.landlord_name,
        ll_parent:        savedLLInfo.parent  || baseTerms.ll_parent,
        ll_relation:      savedLLInfo.relation || baseTerms.ll_relation,
        ll_aadhaar:       savedLLInfo.aadhaar || baseTerms.ll_aadhaar,
        landlord_address: savedLLInfo.address || baseTerms.landlord_address,
        district:         savedLLInfo.district || baseTerms.district,
        taluk:            savedLLInfo.taluk   || baseTerms.taluk,
        state:            savedLLInfo.state   || baseTerms.state,
      };
      mergedLease = { ...lease, terms: JSON.stringify(overlaid) };
    }
    const data = buildAgreementDataFromLease(mergedLease, ten, prop);
    if (!data) { flash("Missing data for PDF", "error"); return; }
    const html = generateAgreementHTML(data, pdfLangs);

    // Use a Blob URL instead of document.write. Reason: when we pass
    // `noopener` to window.open, modern browsers return null as the
    // window handle (for tabnabbing protection), so the old
    // `w.document.write(html)` path silently opened a blank tab.
    //
    // Blob URL lets the browser load the generated HTML as an ordinary
    // page — onload fires correctly (window.print() works), and
    // `noopener` keeps the tabnabbing protection because the new page
    // has no opener reference back.
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank', 'noopener');
    if (!w && !/safari/i.test(navigator.userAgent)) {
      // Popup blocked. Safari returns null for noopener even on success,
      // so we only show the error on non-Safari browsers.
      flash("Allow popups to view agreement", "error");
    }
    // Free the blob memory after the new tab has had time to load it.
    // 60s is generous — the browser caches the resource by then.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  // ── Landlord sign for selected lease ──
  const handleLLSign = (sigData) => {
    if (!sel) return;
    const terms = { ...parseTerms(sel), landlord_signature: sigData };
    const updated = { ...sel, terms: JSON.stringify(terms) };
    setLeases(leases.map(l => l.id === sel.id ? updated : l));
    api.saveLease(updated);
    setShowLLSigPad(false);
    flash("Landlord signature saved \u2705");
  };

  // ── Witness sign ──
  const handleWitSign = (witKey, sigData) => {
    if (!sel) return;
    const sigVal = `sig|${new Date().toLocaleDateString("en-IN")}|${sigData}`;
    const terms = { ...parseTerms(sel), [witKey]: sigVal };
    const updated = { ...sel, terms: JSON.stringify(terms) };
    setLeases(leases.map(l => l.id === sel.id ? updated : l));
    api.saveLease(updated);
    setShowWitSigPad(null);
    flash("Witness signature saved \u2705");
  };

  // \u2500\u2500 Witness name (typed on the Witnesses card, saved on blur).
  // witness1/witness2 names also print on the agreement PDF's
  // "Witnesses:" block \u2014 a signature without a printed full name is
  // weak evidence, hence this field. \u2500\u2500
  const handleWitName = (nameKey, value) => {
    if (!sel) return;
    const v = String(value || "").trim();
    const existing = parseTerms(sel)[nameKey] || "";
    if (v === existing) return; // unchanged \u2014 skip the save round-trip
    const terms = { ...parseTerms(sel), [nameKey]: v };
    const updated = { ...sel, terms: JSON.stringify(terms) };
    setLeases(leases.map(l => l.id === sel.id ? updated : l));
    api.saveLease(updated);
    flash("Witness name saved \u2705");
  };

  // ── Terminate ──
  const handleTerminate = () => {
    if (!sel) return;
    showConfirm("Terminate this agreement?", async () => {
      const updated = { ...sel, status: "terminated" };
      setLeases(leases.map(l => l.id === sel.id ? updated : l));
      await api.saveLease(updated);
      flash("Agreement terminated");
      setView("list");
    });
  };

  // ── Delete ──
  const handleDelete = () => {
    if (!sel) return;
    showConfirm("Delete this agreement permanently?", async () => {
      setLeases(leases.filter(l => l.id !== sel.id));
      await api.deleteLease(sel.id);
      flash("Agreement deleted");
      setView("list");
    });
  };

  // ═══════════════════════════════════════════════════════════════
  // VIEW A: LIST
  // ═══════════════════════════════════════════════════════════════
  if (view === "list") {
    const tabs = [["all", "All"], ["active", `Active (${stats.active})`], ["expiring", `Expiring (${stats.expiring})`], ["draft", `Draft (${stats.draft})`], ["expired", "Expired"]];
    return <div style={{ paddingTop: 16 }}>
      <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>{t.agreements}</h2>

      {/* Landlord Agreement Info */}
      <div style={{ ...crd(h), marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showLLInfoEdit ? 10 : 0 }}>
          <div style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>{"\uD83C\uDFE0"} Landlord Info</div>
          <button onClick={() => { setLLInfo(parseLLInfo()); setShowLLInfoEdit(!showLLInfoEdit); }} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
            {showLLInfoEdit ? "Close" : savedLLInfo.name ? "\u270F\uFE0F Edit" : "+ Setup"}
          </button>
        </div>
        {!showLLInfoEdit && savedLLInfo.name && <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{savedLLInfo.name} {savedLLInfo.parent ? `(${savedLLInfo.relation || "S/o"} ${savedLLInfo.parent})` : ""} {"\u2022"} {savedLLInfo.district || ""}</div>}
        {!showLLInfoEdit && !savedLLInfo.name && <div style={{ color: h.wn, fontSize: 11, marginTop: 4 }}>{"\u26A0\uFE0F"} Not configured — tap Setup to add your details</div>}
        {showLLInfoEdit && <div>
          <input value={llInfo.name || ''} onChange={e => setLI("name", e.target.value)} placeholder="Full Name *" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 13, boxSizing: "border-box", marginBottom: 6 }} />
          <div style={{ display: "flex", gap: 6 }}>
            <input value={llInfo.parent || ''} onChange={e => setLI("parent", e.target.value)} placeholder="Father/Husband" style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 12, boxSizing: "border-box", marginBottom: 6 }} />
            <select value={llInfo.relation || 'son of'} onChange={e => setLI("relation", e.target.value)} style={{ width: 90, padding: "10px 8px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 12, marginBottom: 6 }}><option>son of</option><option>wife of</option><option>daughter of</option></select>
          </div>
          <input value={llInfo.aadhaar || ''} onChange={e => setLI("aadhaar", e.target.value)} placeholder="Aadhaar No" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 13, boxSizing: "border-box", marginBottom: 6 }} />
          <input value={llInfo.address || ''} onChange={e => setLI("address", e.target.value)} placeholder="Address" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 13, boxSizing: "border-box", marginBottom: 6 }} />
          <div style={{ display: "flex", gap: 6 }}>
            <input value={llInfo.district || ''} onChange={e => setLI("district", e.target.value)} placeholder="District" style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 12, boxSizing: "border-box", marginBottom: 6 }} />
            <input value={llInfo.taluk || ''} onChange={e => setLI("taluk", e.target.value)} placeholder="Taluk" style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 12, boxSizing: "border-box", marginBottom: 6 }} />
          </div>
          <input value={llInfo.state || 'Tamil Nadu'} onChange={e => setLI("state", e.target.value)} placeholder="State" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 13, boxSizing: "border-box", marginBottom: 8 }} />
          <button onClick={async () => {
            await api.saveSetting("landlord_info", JSON.stringify(llInfo));
            setSettings(s => ({ ...s, landlord_info: JSON.stringify(llInfo) }));
            setShowLLInfoEdit(false);
            flash("Landlord info saved \u2705");
          }} style={{ width: "100%", padding: 10, borderRadius: 10, border: "none", background: h.ac, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{"\u2705"} Save</button>
        </div>}
      </div>

      {/* Stat cards */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[["active", h.gn, "\u2713", "Active"], ["expiring", "#f59e0b", "!", "Expiring"], ["expired", h.rd, "\u2715", "Expired"]].map(([k, c, ic, lb]) => (
          <div key={k} style={{ flex: 1, padding: "10px 14px", borderRadius: 12, background: `${c}08`, border: `1px solid ${c}20` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <span style={{ color: c, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>{ic} {lb}</span>
            </div>
            <div style={{ color: c, fontSize: 20, fontWeight: 700 }}>{stats[k]}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
        {tabs.map(([k, lb]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: "6px 14px", borderRadius: 999, border: `1px solid ${tab === k ? h.ac : h.bd}`, background: tab === k ? `${h.ac}15` : "transparent", color: tab === k ? h.ac : h.sc, cursor: "pointer", fontSize: 12, fontWeight: tab === k ? 600 : 400, whiteSpace: "nowrap" }}>{lb}</button>
        ))}
      </div>

      {/* Agreement cards */}
      <div data-coach="agreement-list">
      {filtered.length === 0 && <div style={{ ...crd(h), textAlign: "center", padding: 24 }}><Ic n="file" s={32} c={h.sc} /><div style={{ color: h.sc, fontSize: 14, marginTop: 10 }}>No agreements found</div></div>}

      {/* Desktop: dense data table (rows scan far better than cards when
          a landlord has many leases). Mobile keeps the gradient cards
          below. Same openDetail / View-agreement actions per row. */}
      {IS_DESKTOP && filtered.length > 0 && (
        <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", background: h.ib }}>
                {["Tenant", "Property", "Period", "Rent", "Status", ""].map((hd, i) => (
                  <th key={i} style={{ padding: "11px 14px", color: h.sc, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", borderBottom: `1px solid ${h.bd}`, textAlign: hd === "Rent" ? "right" : "left", whiteSpace: "nowrap" }}>{hd}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(l => {
                const tenLive = tens.find(x => x.id === l.tenant_id);
                const ten = resolveTenantIdentity(l, tenLive);
                const prop = props.find(x => x.id === l.property_id);
                const st = getLeaseStatus(l);
                const days = getDaysRemaining(l);
                const stC = leaseStatusColor(st, h);
                return (
                  <tr key={l.id} onClick={() => openDetail(l)}
                    onMouseOver={(e) => { e.currentTarget.style.background = h.ib; }}
                    onMouseOut={(e) => { e.currentTarget.style.background = "transparent"; }}
                    style={{ cursor: "pointer", transition: "background 0.12s" }}>
                    <td style={{ padding: "12px 14px", borderBottom: `1px solid ${h.bd}`, color: h.tx, fontWeight: 600 }}>{ten?.name || "—"}</td>
                    <td style={{ padding: "12px 14px", borderBottom: `1px solid ${h.bd}`, color: h.ts || h.sc, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{prop ? formatPropAddr(prop) : "—"}</td>
                    <td style={{ padding: "12px 14px", borderBottom: `1px solid ${h.bd}`, color: h.ts || h.sc, whiteSpace: "nowrap" }}>
                      {l.start_date} {"→"} {l.end_date || "—"}
                      {days != null && days >= 0 && days <= 30 && <span style={{ color: "#f59e0b", fontWeight: 700, marginLeft: 6 }}>{"⚠️"} {days}d</span>}
                    </td>
                    <td style={{ padding: "12px 14px", borderBottom: `1px solid ${h.bd}`, color: h.tx, fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" }}>{"₹"}{Number(l.rent || 0).toLocaleString()}/mo</td>
                    <td style={{ padding: "12px 14px", borderBottom: `1px solid ${h.bd}` }}><span style={bdg(`${stC}20`, stC)}>{leaseStatusLabel(st, t)}</span></td>
                    <td style={{ padding: "12px 14px", borderBottom: `1px solid ${h.bd}`, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button onClick={(e) => { e.stopPropagation(); setSelId(l.id); viewAgreementInApp(l); }}
                        style={{ background: `${h.ac}12`, border: `1px solid ${h.ac}30`, color: h.ac, padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                        {"📜"} View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!IS_DESKTOP && filtered.map(l => {
        // Snap-first so a terminated lease still shows the tenant's
        // name on the list card. Falls back to live tens for pre-snap
        // legacy leases.
        const tenLive = tens.find(x => x.id === l.tenant_id);
        const ten = resolveTenantIdentity(l, tenLive);
        const prop = props.find(x => x.id === l.property_id);
        const st = getLeaseStatus(l);
        const days = getDaysRemaining(l);
        const stC = leaseStatusColor(st, h);
        // Smart state: active = green, expiring = amber, everything else
        // (draft / expired / terminated) greys out as a muted slate card.
        const tone = st === "active" ? "green" : st === "expiring_soon" ? "amber" : "slate";
        const light = isLightTone(tone);
        // Slate cards need FIXED dark ink — theme tokens flip near-white in
        // dark mode, which rendered grey-on-grey there.
        const tx = light ? SLATE_INK.tx : "#fff";
        const sub = light ? SLATE_INK.sub : "rgba(255,255,255,0.85)";
        return <div key={l.id} onClick={() => openDetail(l)}
          onMouseOver={(e) => gradLift(e, tone, true)}
          onMouseOut={(e) => gradLift(e, tone, false)}
          style={{ ...crd(h), ...gradCardStyle(tone), cursor: "pointer", marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ color: tx, fontWeight: 600, fontSize: 13 }}>{ten?.name || "\u2014"}</div>
              <div style={{ color: sub, fontSize: 11, marginTop: 2 }}>{prop ? formatPropAddr(prop) : "\u2014"}</div>
            </div>
            <span style={light ? bdg(`${stC}20`, stC) : glassPill}>{leaseStatusLabel(st, t)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, color: sub, fontSize: 11 }}>
            <span>{l.start_date} {"\u2192"} {l.end_date || "\u2014"}</span>
            <span style={{ fontWeight: 600, color: tx }}>₹{Number(l.rent || 0).toLocaleString()}/mo</span>
          </div>
          {days != null && days >= 0 && days <= 30 && <div style={{ color: light ? "#f59e0b" : "#fff", fontSize: 10, fontWeight: 600, marginTop: 4 }}>{"\u26A0\uFE0F"} {days} days remaining</div>}
          {/* View Agreement \u2014 opens the in-app document viewer directly.
              stopPropagation so it doesn't fall through to the card's
              openDetail navigation. */}
          <button
            onClick={(e) => { e.stopPropagation(); setSelId(l.id); viewAgreementInApp(l); }}
            style={{
              width: "100%", marginTop: 12,
              background: light ? `${h.ac}12` : "rgba(255,255,255,0.92)",
              border: light ? `1px solid ${h.ac}30` : "1px solid rgba(255,255,255,0.6)",
              color: h.ac, padding: "9px", borderRadius: 10,
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            {"\uD83D\uDCDC"} View Agreement
          </button>
        </div>;
      })}
      </div>
    </div>;
  }

  // ═══════════════════════════════════════════════════════════════
  // VIEW: DOCUMENT — in-app rendered agreement (no download)
  // ═══════════════════════════════════════════════════════════════
  if (view === "document" && sel) {
    // Share a compact agreement summary — same cascade as receipt
    // sharing (txnShare): native share sheet → WhatsApp → clipboard.
    const shareAgreement = async () => {
      const lines = [
        "📜 Rental Agreement",
        "──────────────",
        `Tenant  : ${selTen?.name || "—"}`,
        `Property: ${selProp ? formatPropAddr(selProp) : "—"}`,
        `Rent    : ₹${Number(sel.rent || 0).toLocaleString("en-IN")}/mo`,
        `Deposit : ₹${Number(sel.deposit || 0).toLocaleString("en-IN")}`,
        `Period  : ${sel.start_date || "—"} → ${sel.end_date || "—"}`,
        `Status  : ${leaseStatusLabel(getLeaseStatus(sel), t)}`,
        "──────────────",
        `via ${orgName || "ProRentz"}`,
      ].join("\n");
      if (navigator.share) {
        try { await navigator.share({ title: "Rental Agreement", text: lines }); return; }
        catch (e) { if (e?.name === "AbortError") return; }
      }
      try { window.open(`https://wa.me/?text=${encodeURIComponent(lines)}`, "_blank", "noopener,noreferrer"); return; } catch (e) { /* fall through */ }
      try { await navigator.clipboard.writeText(lines); flash("Agreement summary copied"); }
      catch (e) { flash("Could not share", "error"); }
    };

    return <div style={{ paddingTop: 16, display: "flex", flexDirection: "column", height: "calc(100vh - 120px)" }}>
      {/* Header: back + Edit + Share + Download */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8 }}>
        <button onClick={() => setView("detail")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {"←"} {t.back}
        </button>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {/* Edit returns to the detail screen — the "full edit" surface
              where terms, clauses, photos, witnesses and signatures are
              all editable (per user request). */}
          <button onClick={() => setView("detail")} style={{ background: `${h.ac}15`, border: `1px solid ${h.ac}`, color: h.ac, padding: "6px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"✏️"} {t.edit || "Edit"}</button>
          <button onClick={shareAgreement} style={{ background: `${h.gn || "#16a34a"}15`, border: `1px solid ${h.gn || "#16a34a"}`, color: h.gn || "#16a34a", padding: "6px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"📤"} {t.share || "Share"}</button>
          <button onClick={() => printAgreement(sel)} style={{ background: h.ac, border: "none", color: "#fff", padding: "6px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"⬇️"} Download</button>
        </div>
      </div>

      {/* The agreement, rendered in-app via a sandboxed iframe so its
          own <style> / page CSS can't leak into the app. srcDoc loads
          the exact HTML the PDF uses, so what you see = what downloads. */}
      <div style={{ flex: 1, borderRadius: 12, overflow: "hidden", border: `1px solid ${h.bd}`, background: "#fff" }}>
        <iframe
          title="Rental Agreement"
          srcDoc={docHtml}
          sandbox="allow-same-origin"
          style={{ width: "100%", height: "100%", border: "none", display: "block" }}
        />
      </div>
    </div>;
  }

  // ═══════════════════════════════════════════════════════════════
  // VIEW B: DETAIL
  // ═══════════════════════════════════════════════════════════════
  if (view === "detail" && sel) {
    const st = getLeaseStatus(sel);
    const stC = leaseStatusColor(st, h);
    const days = getDaysRemaining(sel);
    const terms = { ...defaultTerms, ...parseTerms(sel) };
    const llSig = terms.landlord_signature;
    const tenSig = selTen?.esign;

    const row = (label, val) => <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${h.bd}30` }}>
      <span style={{ color: h.sc, fontSize: 12 }}>{label}</span>
      <span style={{ color: h.tx, fontSize: 12, fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>{val || "\u2014"}</span>
    </div>;

    return <div style={{ paddingTop: 16 }}>
      <button onClick={() => setView("list")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>{t.agreements}</h2>
        <span style={bdg(`${stC}20`, stC)}>{leaseStatusLabel(st, t)}</span>
      </div>

      {/* Expiring warning */}
      {isExpiringSoon(sel) && <div style={{ background: `${h.wn}15`, borderRadius: 10, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14 }}>{"\u26A0\uFE0F"}</span>
        <span style={{ color: h.wn, fontSize: 12, fontWeight: 600 }}>{days} days remaining</span>
      </div>}

      {/* HERO — property photo carrying the address + status, with the
          tenant and the three numbers that actually matter underneath.
          Everything else on this page is collapsed by default. */}
      {(() => {
        const photo = selProp?.photos && selProp.photos[0];
        const cell = (label, value) => (
          <div style={{ flex: 1, minWidth: 0, padding: "10px 6px", textAlign: "center" }}>
            <div style={{ color: h.tx, fontSize: 13, fontWeight: 700, letterSpacing: "-0.2px", fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
            <div style={{ color: h.ts, fontSize: 10, fontWeight: 500, marginTop: 2 }}>{label}</div>
          </div>
        );
        return (
          <div style={{ background: h.cd, borderRadius: 22, overflow: "hidden", boxShadow: h.sh, marginBottom: 12 }}>
            <div style={{ position: "relative", aspectRatio: "16/9", background: photo ? "#000" : `${h.ac}0d`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              {photo
                ? <img src={photo} alt={selProp ? formatPropAddr(selProp) : "Property"} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                : <Ic n="bldg" s={34} c={h.ac} />}
              <span style={{
                position: "absolute", top: 10, left: 10, background: `${stC}f0`, color: "#fff",
                padding: "4px 10px", borderRadius: 999, fontSize: 9.5, fontWeight: 700,
                letterSpacing: "0.4px", textTransform: "uppercase",
                backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              }}>{leaseStatusLabel(st, t)}</span>
              <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "30px 14px 11px", background: "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.25) 55%, transparent)" }}>
                <div style={{ color: "#fff", fontSize: 15, fontWeight: 700, letterSpacing: "-0.3px", lineHeight: 1.25 }}>{selProp ? formatPropAddr(selProp) : "—"}</div>
                <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 11.5, marginTop: 2 }}>
                  {selProp?.rooms ? `${selProp.rooms} rooms · ` : ""}{sel.lease_type === "commercial" ? "Commercial" : "Residential"}
                </div>
              </div>
            </div>

            {/* Tenant */}
            <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 14px" }}>
              <div style={{
                width: 38, height: 38, borderRadius: "50%", flexShrink: 0, background: h.gl, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700,
                overflow: "hidden",
              }}>
                {selTen?.avatar_url
                  ? <img src={selTen.avatar_url} alt={selTen?.name || "Tenant"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : (selTen?.name || "?").trim().charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: h.tx, fontSize: 13.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selTen?.name || "—"}</div>
                <div style={{ color: h.ts, fontSize: 11.5 }}>{selTen?.ph || "No phone"}</div>
              </div>
              {selTen?.ph && (
                <a href={"tel:" + String(selTen.ph).replace(/[^\d+]/g, "")} aria-label="Call tenant"
                  style={{ width: 34, height: 34, borderRadius: 17, background: `${h.gn}12`, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", flexShrink: 0 }}>
                  <span style={{ fontSize: 14 }}>{"📞"}</span>
                </a>
              )}
            </div>

            <div style={{ display: "flex", borderTop: `1px solid ${h.bd}` }}>
              {cell(t.rent || "Rent", `₹${Number(sel.rent || 0).toLocaleString()}`)}
              <div style={{ width: 1, background: h.bd }} />
              {cell(t.deposit || "Deposit", `₹${Number(sel.deposit || 0).toLocaleString()}`)}
              <div style={{ width: 1, background: h.bd }} />
              {cell("Ends", sel.end_date || "—")}
            </div>
          </div>
        );
      })()}

      {/* Readiness — what's still missing, at a glance */}
      {(() => {
        const cPhotos = Array.isArray(terms.condition_photos) ? terms.condition_photos.length : 0;
        const invRooms = (terms.inventory && Array.isArray(terms.inventory.rooms)) ? terms.inventory.rooms : null;
        const invItems = invRooms ? invRooms.reduce((s, r) => s + ((r.items || []).length), 0) : 0;
        const invShot = invRooms ? invRooms.reduce((s, r) => s + (r.items || []).reduce((n, it) => n + ((it.photos || []).length), 0), 0) : 0;
        const clauseKeys = ["maintenance_clause", "usage_clause", "electricity_clause", "property_tax_clause", "painting_clause", "special_terms", "fittings"];
        const clauseN = clauseKeys.filter(k => terms[k]).length + (terms.custom_clauses || []).filter(cc => cc.title || cc.text).length;
        return <Readiness h={h} onEdit={openEdit} steps={[
          { short: "Dates", label: "lease dates", done: !!(sel.start_date && sel.end_date) },
          { short: "Money", label: "rent & deposit", done: Number(sel.rent) > 0 && Number(sel.deposit) > 0 },
          { short: "Clauses", label: "agreement clauses", done: clauseN > 0 },
          { short: "Photos", label: "room photos", done: cPhotos > 0 },
          // Items counts as done once the list exists; item photos raise the
          // quality of the record but shouldn't block "ready".
          { short: "Items", label: `inventory items${invShot > 0 ? ` (${invShot} photos)` : ""}`, done: invItems > 0 },
          { short: "You", label: "your signature", done: !!llSig },
          { short: "Tenant", label: "tenant signature", done: !!tenSig },
        ]} />;
      })()}

      {/* Lease Terms — collapsed; pencil lives inside the expanded body */}
      <Section h={h} icon="📄" title="Lease Terms"
        summary={`${sel.start_date || "—"} → ${sel.end_date || "—"}`}
        onEdit={openEdit} editLabel={t.edit || "Edit"}>
        {row("Start Date", sel.start_date)}
        {row("End Date", sel.end_date)}
        {row("Rent", `₹${Number(sel.rent || 0).toLocaleString()}/mo`)}
        {row("Deposit", `₹${Number(sel.deposit || 0).toLocaleString()}`)}
        {row("Notice Period", `${sel.notice_period_days || 30} days`)}
        {sel.lock_in_months && row("Lock-in", `${sel.lock_in_months} months`)}
        {sel.escalation_pct && row("Escalation", `${sel.escalation_pct}% every ${sel.escalation_interval_months || 12} months`)}
        {row("Type", sel.lease_type === "commercial" ? "Commercial" : "Residential")}
      </Section>

      {/* Agreement Clauses — inline editable */}
      {(() => {
        const clauseFields = [
          { key: "maintenance_clause", label: "Maintenance" },
          { key: "usage_clause", label: "Usage" },
          { key: "electricity_clause", label: "Electricity / Water" },
          { key: "property_tax_clause", label: "Property Tax" },
          { key: "painting_clause", label: "Painting / Repair" },
          { key: "special_terms", label: "Special Terms" },
          { key: "fittings", label: "Fittings & Fixtures" },
        ];
        const hasAny = clauseFields.some(c => terms[c.key]);
        const filled = clauseFields.filter(c => terms[c.key]).length + (terms.custom_clauses || []).filter(cc => cc.title || cc.text).length;
        return <Section h={h} icon={"\uD83D\uDCCB"} title="Agreement Clauses"
          summary={filled > 0 ? `${filled} clause${filled === 1 ? "" : "s"}` : "None added yet"}
          onEdit={openEdit} editLabel="Edit all">
          {clauseFields.map(c => {
            // A clause may be stored as a plain string (legacy / custom English)
            // OR as an object { en, [langCode]: "..." } when the landlord
            // added a translation. Extract something React can render.
            const raw = terms[c.key];
            if (!raw) return null;
            const enText = (typeof raw === 'object') ? (raw.en || '') : raw;
            const transLangs = (typeof raw === 'object')
                ? Object.keys(raw).filter(k => k !== 'en' && raw[k])
                : [];
            return (
              <div key={c.key} style={{ marginBottom: 8 }}>
                <div style={{ color: h.tx, fontSize: 11, fontWeight: 600, marginBottom: 2 }}>
                  {c.label}
                  {transLangs.length > 0 && (
                    <span style={{ color: h.gn, fontSize: 10, fontWeight: 500, marginLeft: 6 }}>
                      + {transLangs.join(', ').toUpperCase()} translation
                    </span>
                  )}
                </div>
                <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{enText}</div>
              </div>
            );
          })}
          {/* Custom clauses */}
          {(terms.custom_clauses || []).map((cc, i) => cc.title || cc.text ? <div key={`cc_${i}`} style={{ marginBottom: 8 }}>
            <div style={{ color: h.tx, fontSize: 11, fontWeight: 600, marginBottom: 2 }}>{cc.title || `Custom Clause ${i + 1}`}</div>
            <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{cc.text}</div>
          </div> : null)}
          {!hasAny && !(terms.custom_clauses || []).length && <div style={{ color: h.sc, fontSize: 11, fontStyle: "italic" }}>No clauses added yet. Tap "Edit all" to add.</div>}
        </Section>;
      })()}

      {/* Property Condition — ONE record: room photos + the items in each
          room, with a condition on every item. Replaces the two separate
          cards (photos / inventory) that made people think they were
          documenting the same thing twice. Storage is unchanged: photos to
          terms.condition_photos, items to terms.inventory. */}
      <PropertyCondition
        lease={sel}
        editable={true}
        addedBy="landlord"
        onSave={async (updated) => {
          setLeases(leases.map(l => l.id === sel.id ? updated : l));
          await api.saveLease(updated);
        }}
      />

      {/* Witnesses — optional, opt-in via "+ Add witness" */}
      <Witnesses
        h={h} sel={sel} terms={terms}
        handleWitName={handleWitName} handleWitSign={handleWitSign}
        showWitSigPad={showWitSigPad} setShowWitSigPad={setShowWitSigPad}
        SigPad={SignaturePad}
      />

      {/* Signatures — collapsed; the summary says who still has to sign */}
      <Section h={h} icon={"✍️"} title="Signatures"
        summary={llSig && tenSig ? "Both parties signed" : llSig ? "Awaiting tenant signature" : tenSig ? "Awaiting your signature" : "Not signed yet"}>
        {/* Landlord */}
        <div style={{ marginBottom: 10 }}>
          {llSig ? <div>
            <div style={{ background: `${h.gn}10`, padding: 10, borderRadius: 8, border: `1px solid ${h.gn}25` }}>
              <div style={{ color: h.gn, fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{"\u2713"} Landlord Signed</div>
              {llSig.startsWith("sig|") && <img src={llSig.split("|").slice(2).join("|")} alt="sig" style={{ maxHeight: 50, background: "#fff", padding: 4, borderRadius: 6, display: "block" }} />}
              {llSig.startsWith("sig|") && <div style={{ color: h.sc, fontSize: 9, marginTop: 2 }}>Date: {llSig.split("|")[1]}</div>}
            </div>
            <button onClick={() => setShowLLSigPad(true)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 11, fontWeight: 600, padding: 0, marginTop: 4 }}>{"\u270F\uFE0F"} Re-Sign</button>
          </div> : <button onClick={() => setShowLLSigPad(true)} style={{ ...btnP(h), width: "100%", padding: 10 }}>{"\u270D\uFE0F"} Sign as Landlord</button>}
        </div>
        {/* Tenant */}
        <div>
          {tenSig ? <div>
            <div style={{ background: `${h.gn}10`, padding: 10, borderRadius: 8, border: `1px solid ${h.gn}25` }}>
              <div style={{ color: h.gn, fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{"\u2713"} Tenant Signed</div>
              {tenSig.startsWith("sig|") && <img src={tenSig.split("|").slice(2).join("|")} alt="sig" style={{ maxHeight: 50, background: "#fff", padding: 4, borderRadius: 6, display: "block" }} />}
              {!tenSig.startsWith("sig|") && <img src={tenSig} alt="sig" style={{ maxHeight: 50, background: "#fff", padding: 4, borderRadius: 6, display: "block" }} />}
              {tenSig.startsWith("sig|") && <div style={{ color: h.sc, fontSize: 9, marginTop: 2 }}>Date: {tenSig.split("|")[1]}</div>}
            </div>
            <div style={{ color: h.sc, fontSize: 10, marginTop: 4, fontStyle: "italic" }}>Tenant can re-sign from their own login</div>
          </div> : <div style={{ background: `${h.wn}10`, padding: 10, borderRadius: 8, border: `1px solid ${h.wn}25` }}>
            <div style={{ color: h.wn, fontSize: 11, fontWeight: 600 }}>{"\u23F3"} Awaiting tenant signature</div>
            <div style={{ color: h.sc, fontSize: 10, marginTop: 4 }}>Tenant signs from their app: My Agreement {"\u2192"} Sign & Activate</div>
          </div>}
        </div>
      </Section>

      {/* Landlord sig pad modal */}
      {showLLSigPad && <div style={{ ...crd(h), border: `2px solid ${h.ac}40` }}>
        <SignaturePad h={h} onSave={(data) => handleLLSign(`sig|${new Date().toLocaleDateString("en-IN")}|${data}`)} onCancel={() => setShowLLSigPad(false)} label="Landlord Signature" />
      </div>}

      {/* PDF language picker — toggle chips. Selection is remembered
          across sessions (localStorage). Used when the PDF button below
          assembles the agreement document. */}
      {(() => {
        // Collapsed-by-default summary; tap to reveal the full chip grid.
        const selectedLabels = AVAILABLE_LANGUAGES
          .filter(l => pdfLangs.includes(l.code))
          .map(l => l.nativeLabel);
        const summary = selectedLabels.length === 0
          ? "None \u2014 pick at least one"
          : selectedLabels.join(" + ");
        return (
          <div style={{ marginBottom: 8 }}>
            <button
              type="button"
              onClick={() => setShowLangPicker(v => !v)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "10px 14px",
                borderRadius: 12,
                border: `1px solid ${h.bd}`,
                background: h.ib,
                color: h.tx,
                fontSize: 13,
                cursor: "pointer",
                textAlign: "left",
              }}
              aria-expanded={showLangPicker}
            >
              <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span style={{ color: h.sc, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  PDF languages
                </span>
                <span style={{
                  color: selectedLabels.length === 0 ? h.wn : h.tx,
                  fontWeight: 600,
                  marginTop: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {summary}
                </span>
              </span>
              <span style={{ color: h.sc, fontSize: 14, flexShrink: 0 }}>
                {showLangPicker ? "\u25b4" : "\u25be"}
              </span>
            </button>
            {showLangPicker && (
              <div style={{
                display: "flex", flexWrap: "wrap", gap: 8,
                marginTop: 8, padding: "10px 4px",
              }}>
                {AVAILABLE_LANGUAGES.map(lang => {
                  const on = pdfLangs.includes(lang.code);
                  return (
                    <button
                      key={lang.code}
                      onClick={() => toggleLang(lang.code)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 999,
                        border: `1px solid ${on ? h.ac : h.bd}`,
                        background: on ? `${h.ac}18` : h.ib,
                        color: on ? h.ac : h.tx,
                        fontSize: 12,
                        fontWeight: on ? 700 : 500,
                        cursor: "pointer",
                        transition: "0.15s",
                      }}
                      type="button"
                      aria-pressed={on}
                    >
                      {on ? "\u2713 " : ""}{lang.nativeLabel}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Actions */}
      {/* Primary: View the formatted agreement inside the app. */}
      {/* Edit is no longer here \u2014 each section owns its own pencil, so the
          action bar stays about the document itself. */}
      <button onClick={() => viewAgreementInApp(sel)} style={{
        width: "100%", padding: "13px 16px", borderRadius: 16, border: "none",
        background: h.ac, color: "#fff", fontSize: 14, fontWeight: 700,
        fontFamily: "inherit", cursor: "pointer", marginBottom: 8,
        boxShadow: `0 8px 20px ${h.ac}38`,
      }}>View Agreement</button>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <button onClick={() => printAgreement(sel)} style={{
          flex: 1, padding: "11px 14px", borderRadius: 14, border: "none",
          background: `${h.ac}0f`, color: h.a2, fontSize: 12.5, fontWeight: 600,
          fontFamily: "inherit", cursor: "pointer",
        }}>Download PDF</button>
      </div>

      {/* Legal standing — say plainly what this document is and isn't. */}
      <div style={{
        display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 10,
        background: h.dk ? "rgba(255,255,255,0.04)" : "rgba(17,24,39,0.03)",
        borderRadius: 14, padding: "11px 13px",
      }}>
        <span style={{ fontSize: 14, lineHeight: 1.2, flexShrink: 0 }}>{"ℹ️"}</span>
        <span style={{ color: h.ts, fontSize: 11, lineHeight: 1.55 }}>
          This is a <strong style={{ color: h.tx, fontWeight: 600 }}>digital record</strong> of the agreement between you and your
          tenant. To make it a formal document, download it, take a print, and have both parties sign the printed copy —
          keep one each. Stamp duty and registration, where your state requires them, still apply to the printed copy.
        </span>
      </div>

      {/* Settlement Receipt \u2014 only relevant for leases that went
          through the vacate flow and have a deposit_settlements row.
          The receipt is the immutable record of deductions, refund
          amount, and both signatures at move-out; landlords need it
          for tax/audit/dispute history. We show the button for any
          terminated lease and let the server respond with 'no
          settlement on file' if the user terminated outside the
          vacate flow. */}
      {sel.status === "terminated" && (
        <div style={{ marginBottom: 8 }}>
          <button
            onClick={() => openSettlementReceipt(sel)}
            disabled={openingReceipt}
            style={{
              ...btnSec(h),
              width: "100%",
              padding: 10,
              color: h.gn,
              borderColor: `${h.gn}40`,
              background: `${h.gn}08`,
              opacity: openingReceipt ? 0.6 : 1,
              cursor: openingReceipt ? "wait" : "pointer",
            }}
          >
            {openingReceipt ? "Loading receipt\u2026" : `${"\uD83E\uDDFE"} View Settlement Receipt`}
          </button>
          <div style={{ color: h.sc, fontSize: 10, marginTop: 4, textAlign: "center" }}>
            Deductions, refund amount, and signatures at move-out
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {sel.status !== "terminated" && <button onClick={handleTerminate} style={{
          flex: 1, padding: "11px 14px", borderRadius: 14, border: "none",
          background: `${h.wn}0f`, color: h.wn, fontSize: 12.5, fontWeight: 600,
          fontFamily: "inherit", cursor: "pointer",
        }}>Terminate</button>}
        <button onClick={handleDelete} style={{
          flex: 1, padding: "11px 14px", borderRadius: 14, border: "none",
          background: `${h.rd}0f`, color: h.rd, fontSize: 12.5, fontWeight: 600,
          fontFamily: "inherit", cursor: "pointer",
        }}>Delete</button>
      </div>
    </div>;
  }

  // ═══════════════════════════════════════════════════════════════
  // VIEW C: EDIT
  // ═══════════════════════════════════════════════════════════════
  if (view === "edit") {
    const sec = (title) => <div style={{ color: h.ac, fontWeight: 700, fontSize: 13, marginTop: 16, marginBottom: 8 }}>{title}</div>;
    return <div style={{ paddingTop: 16 }}>
      <button onClick={() => setView("detail")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>Edit Agreement</h2>

      {/* Lease Fields */}
      <div style={crd(h)}>
        {sec("Lease Terms")}
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><label style={{ color: h.sc, fontSize: 11, display: "block", marginBottom: 4 }}>Start Date</label><input type="date" value={editLease.start_date || ''} onChange={e => setEL("start_date", e.target.value)} style={inpS(h)} /></div>
          <div style={{ flex: 1 }}><label style={{ color: h.sc, fontSize: 11, display: "block", marginBottom: 4 }}>End Date</label><input type="date" value={editLease.end_date || ''} onChange={e => setEL("end_date", e.target.value)} style={inpS(h)} /></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><Fld h={h} l="Rent (₹)" v={editLease.rent} set={v => setEL("rent", Number(v))} type="number" /></div>
          <div style={{ flex: 1 }}><Fld h={h} l="Deposit (₹)" v={editLease.deposit} set={v => setEL("deposit", Number(v))} type="number" /></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><Fld h={h} l="Notice (days)" v={editLease.notice_period_days} set={v => setEL("notice_period_days", Number(v))} type="number" /></div>
          <div style={{ flex: 1 }}><Fld h={h} l="Lock-in (months)" v={editLease.lock_in_months} set={v => setEL("lock_in_months", Number(v))} type="number" /></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><Fld h={h} l="Escalation %" v={editLease.escalation_pct} set={v => setEL("escalation_pct", Number(v))} type="number" /></div>
          <div style={{ flex: 1 }}><Fld h={h} l="Esc. Interval (mo)" v={editLease.escalation_interval_months} set={v => setEL("escalation_interval_months", Number(v))} type="number" /></div>
        </div>
        <label style={{ color: h.sc, fontSize: 11, display: "block", marginBottom: 4, marginTop: 4 }}>Status</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {["draft", "active"].map(s => (
            <button key={s} onClick={() => setEL("status", s)} style={{ flex: 1, padding: 8, borderRadius: 10, border: `1px solid ${editLease.status === s ? h.ac : h.bd}`, background: editLease.status === s ? `${h.ac}15` : "transparent", color: editLease.status === s ? h.ac : h.sc, cursor: "pointer", fontSize: 12, textTransform: "capitalize" }}>{s}</button>
          ))}
        </div>
      </div>

      {/* Landlord Info — auto-populated from Settings */}
      <div style={crd(h)}>
        {sec("Landlord Information")}
        {savedLLInfo.name ? <>
          <div style={{ background: `${h.gn}08`, borderRadius: 8, padding: 10, border: `1px solid ${h.gn}20`, marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: h.gn, fontSize: 10, fontWeight: 700 }}>{"\u2705"} Using saved landlord info</span>
              <button onClick={() => go("settings")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{"\u2699\uFE0F"} Edit in Settings</button>
            </div>
          </div>
          <div style={{ fontSize: 12, color: h.tx, lineHeight: 1.8 }}>
            <div><b>{savedLLInfo.name}</b> {savedLLInfo.parent ? `(${savedLLInfo.relation || "S/o"} ${savedLLInfo.parent})` : ""}</div>
            {savedLLInfo.aadhaar && <div style={{ color: h.sc }}>Aadhaar: {savedLLInfo.aadhaar}</div>}
            {savedLLInfo.address && <div style={{ color: h.sc }}>{savedLLInfo.address}</div>}
            {(savedLLInfo.district || savedLLInfo.taluk) && <div style={{ color: h.sc }}>{[savedLLInfo.taluk, savedLLInfo.district, savedLLInfo.state].filter(Boolean).join(", ")}</div>}
          </div>
        </> : <div style={{ background: `${h.wn}10`, borderRadius: 8, padding: 12, border: `1px solid ${h.wn}25` }}>
          <div style={{ color: h.wn, fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{"\u26A0\uFE0F"} Landlord info not configured</div>
          <div style={{ color: h.sc, fontSize: 11, marginBottom: 8 }}>Go to Settings to add your name, aadhaar, address. It will auto-fill in all agreements.</div>
          <button onClick={() => go("settings")} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${h.ac}`, background: `${h.ac}10`, color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"\u2699\uFE0F"} Go to Settings</button>
        </div>}
      </div>

      {/* Tenant Info */}
      <div style={crd(h)}>
        {sec("Tenant Agreement Details")}
        {/* Subtle hint so the landlord understands typing here doesn't
            just fill THIS lease — empty slots on the tenant's profile
            get backfilled too, so the next agreement for this tenant
            (renewal, new property) auto-populates. We "fill empty only"
            on the server, so a value the tenant entered themselves is
            never overwritten. */}
        <div style={{ color: h.sc, fontSize: 10, marginBottom: 10, fontStyle: "italic", lineHeight: 1.5 }}>
          {"💡"} These also fill the tenant's profile (only when empty there), so future agreements for this tenant don't need re-typing. Saved permanently — preserved even after termination.
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><Fld h={h} l="Tenant's Parent" v={editTerms.ten_parent} set={v => setET("ten_parent", v)} /></div>
          <div style={{ flex: 1 }}>
            <label style={{ color: h.sc, fontSize: 12, fontWeight: 600, display: "block", marginBottom: 5, letterSpacing: "0.3px", textTransform: "uppercase" }}>Relation</label>
            <select value={editTerms.ten_relation || 'son of'} onChange={e => setET("ten_relation", e.target.value)} style={inpS(h)}>
              <option>son of</option><option>wife of</option><option>daughter of</option><option>husband of</option>
            </select>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><Fld h={h} l="Tenant Aadhaar" v={editTerms.ten_aadhaar} set={v => setET("ten_aadhaar", v)} /></div>
          <div style={{ flex: 1 }}><Fld h={h} l="Tenant Address" v={editTerms.ten_address} set={v => setET("ten_address", v)} /></div>
        </div>
      </div>

      {/* Clauses */}
      <div style={crd(h)}>
        {sec("Agreement Clauses")}
        <ClauseFld h={h} l="Maintenance"     v={editTerms.maintenance_clause}  set={v => setET("maintenance_clause", v)} />
        <ClauseFld h={h} l="Usage"           v={editTerms.usage_clause}        set={v => setET("usage_clause", v)} />
        <ClauseFld h={h} l="Electricity/Water" v={editTerms.electricity_clause} set={v => setET("electricity_clause", v)} />
        <ClauseFld h={h} l="Property Tax"    v={editTerms.property_tax_clause} set={v => setET("property_tax_clause", v)} />
        <ClauseFld h={h} l="Painting/Repair" v={editTerms.painting_clause}     set={v => setET("painting_clause", v)} />
        <ClauseFld h={h} l="Special Terms"   v={editTerms.special_terms}       set={v => setET("special_terms", v)} />
        <Fld h={h} l="Fittings & Fixtures" v={editTerms.fittings} set={v => setET("fittings", v)} multiline />

        {/* Custom Clauses */}
        {(editTerms.custom_clauses || []).map((cc, i) => (
          <div key={i} style={{ position: "relative", marginTop: 8, padding: 10, borderRadius: 10, border: `1px solid ${h.bd}40`, background: `${h.bg}` }}>
            <button onClick={() => {
              const updated = [...(editTerms.custom_clauses || [])];
              updated.splice(i, 1);
              setET("custom_clauses", updated);
            }} style={{ position: "absolute", top: 6, right: 8, background: "none", border: "none", color: h.rd, cursor: "pointer", fontSize: 14, fontWeight: 700 }}>{"\u2715"}</button>
            <Fld h={h} l={`Clause ${i + 1} Title`} v={cc.title} set={v => {
              const updated = [...(editTerms.custom_clauses || [])];
              updated[i] = { ...updated[i], title: v };
              setET("custom_clauses", updated);
            }} ph="e.g. Pet Policy" />
            <Fld h={h} l="Description" v={cc.text} set={v => {
              const updated = [...(editTerms.custom_clauses || [])];
              updated[i] = { ...updated[i], text: v };
              setET("custom_clauses", updated);
            }} multiline ph="Enter clause details..." />
          </div>
        ))}

        <button onClick={() => {
          const updated = [...(editTerms.custom_clauses || []), { title: "", text: "" }];
          setET("custom_clauses", updated);
        }} style={{ ...btnSec(h), width: "100%", marginTop: 12, padding: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <span style={{ fontSize: 16 }}>+</span> Add Clause
        </button>
      </div>

      {/* Save/Cancel */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        <button onClick={saveEdit} style={{ ...btnP(h), flex: 2, padding: 12 }}>{"\u2705"} Save Agreement</button>
        <button onClick={() => setView("detail")} style={{ ...btnSec(h), flex: 1, padding: 12 }}>Cancel</button>
      </div>
    </div>;
  }

  // Fallback
  return <div style={{ paddingTop: 16 }}><button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13 }}>&larr; {t.back}</button></div>;
}

export default AgreementPage;
